/**
 * plans.service.ts
 *
 * Typed API call wrappers for the plans domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope — callers never see Axios wrappers.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';

// ─── Domain types ─────────────────────────────────────────────────────────────

export interface Plan {
  planId: number;
  patientId: number;
  patientName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  accountId: number;
  name: string | null;
  stageId: number | null;
  stageName: string | null;
  date: string | null;
  lastContact: string | null;
  doctorId: number | null;
  doctorName: string | null;
  coordId: number | null;
  coordName: string | null;
  nurseId: number | null;
  nurseName: string | null;
  facilityId: number | null;
  facilityName: string | null;
  planTypeId: number | null;
  planTypeName: string | null;
  proc1Id: number | null;
  proc1Name: string | null;
  proc2Id: number | null;
  proc2Name: string | null;
  proc3Id: number | null;
  proc3Name: string | null;
  totalQuote: number | null;
  timeframe: string | null;
  timeframeName: string | null;
  ballpark: number | null;
  financing: boolean;
  interestLevel: number | null;
  revenueOpportunity: number | null;
  displayId: string | null;
  lcSubject: string | null;
  lcType: string | null;
  referralGeneral: number | null;
  referralSpecific: number | null;
  referralCategoryName: string | null;
  referralSourceName: string | null;
  referralFirstname: string | null;
  referralLastname: string | null;
  referralOther: string | null;
  /** The referring patient's own patientID (`plans.refSourceID`). */
  refSourceId: number | null;
  /**
   * The referring patient resolved from `refSourceId`. Null unless the plan's category
   * is the one meaning "referred by an existing patient" — see `isPatientReferralCategory`
   * on the lead source category.
   */
  referralPatient: {
    patientId: number;
    firstName: string;
    lastName: string;
    displayId: number | null;
    dateOfBirth: string | null;
  } | null;
  leadSourceId: number | null;
  recepId: number | null;
  receptionistName: string | null;
  inquiryNote: string | null;
}

/** Lighter version used in list views — omits rarely-needed join fields */
export interface PlanListItem {
  planId: number;
  patientId: number;
  patientName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  accountId: number;
  name: string | null;
  stageId: number | null;
  stageName: string | null;
  stageSubId: number | null;
  stageSubName: string | null;
  date: string | null;
  lastContact: string | null;
  doctorId: number | null;
  doctorName: string | null;
  coordId: number | null;
  coordName: string | null;
  facilityId: number | null;
  facilityName: string | null;
  planTypeId: number | null;
  planTypeName: string | null;
  proc1Name: string | null;
  proc2Name: string | null;
  proc3Name: string | null;
  totalQuote: number | null;
  timeframe: string | null;
  timeframeName: string | null;
  interestLevel: number | null;
  revenueOpportunity: number | null;
  displayId: string | null;
  lcType: string | null;
}

export interface StageStats {
  stageId: number;
  stageName: string;
  count: number;
}

/**
 * One pill in the plan progress pipeline. Mirrors a single
 * `accounts_custom_stages` row enriched with the per-pipeline
 * `accounts_stage_rules` overrides legacy plan-stage-pipeline.php applies.
 *
 *  - accountCustomStageId : the per-account stage ID (rules use this as the key)
 *  - stageId              : the legacy global stages.stageID (0 if no mapping)
 *  - selected             : the plan is currently parked on this pill
 *  - moveTo               : pill is a valid jump target from the current pill
 *                           (clickable; greyed-but-clickable in the legacy bar)
 *  - showArrow            : legacy `<div class="stages-arrow show">` flag
 *  - hiddenList           : pill is collapsed into the trailing "..." popover
 */
export interface PlanProgressStage {
  accountCustomStageId: number;
  stageId: number;
  label: string;
  orderNum: number;
  selected: boolean;
  moveTo: boolean;
  showArrow: boolean;
  hiddenList: boolean;
}

export interface PlanProgressBar {
  currentStageId: number | null;
  currentAccountCustomStageId: number | null;
  currentStageName: string | null;
  pipelineTypeId: number;
  stageSubId: number;
  stages: PlanProgressStage[];
}

/** One option in the stage-sub-status dropdown that lives under the pipeline. */
export interface PlanStageSub {
  stageSubId: number;
  name: string;
}

export interface PlanStageSubsResponse {
  currentStageSubId: number;
  options: PlanStageSub[];
}

export interface CreatePlanPayload {
  patientId: number;
  name?: string;
  stageId?: number;
  doctorId?: number;
  coordId?: number;
  nurseId?: number;
  facilityId?: number;
  planTypeId?: number | null;
  proc1Id?: number;
  proc2Id?: number;
  proc3Id?: number;
  timeframe?: string;
  ballpark?: number;
  financing?: boolean;
  interestLevel?: number;
  /**
   * A referral is three independent values and each has its own column, exactly as
   * legacy posts them: `refSourceId` is the referring PATIENT's patientID,
   * `referralGeneral` the category and `referralSpecific` the specific source. Sending
   * one value for two of them is what silently unlinked referring patients before.
   */
  refSourceId?: number;
  leadSourceId?: number;
  referralGeneral?: number;
  referralSpecific?: number;
  inquiryNote?: string;
}

export interface UpdatePlanPayload extends Partial<CreatePlanPayload> {
  referralFirstname?: string;
  referralLastname?: string;
  stageSubId?: number;
  lastContact?: string;
  lcSubject?: string;
  lcAuthor?: string;
  lcMessage?: string;
  lcType?: string;
  totalQuote?: number;
  revenueOpportunity?: number;
  messageNotes?: string;
  messageSubject?: string;
  alerts?: string;
  recipients?: string;
}

/**
 * Plan as returned by `PUT /plans/{id}` — same shape as Plan plus an optional
 * `newPlanId` populated when the stage change spawned a revision (stage 12).
 * Callers should swap the card to `newPlanId` when present.
 */
export interface UpdatedPlan extends Plan {
  newPlanId?: number;
}

export interface PlanListParams {
  search?: string;
  stageId?: number;
  doctorId?: number;
  coordId?: number;
  facilityId?: number;
  page?: number;
  perPage?: number;
  export?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  planTypeIds?: string[];
  planTypeIdsNegate?: boolean;
  leadSourceIds?: string[];
  leadSourceGroupIds?: string[];
  leadSourceNegate?: boolean;
  statusIds?: string[];
  statusIdsNegate?: boolean;
  appointmentTypeIds?: string[];
  appointmentTypeIdsNegate?: boolean;
  membershipIds?: string[];
  membershipIdsNegate?: boolean;
}

export interface StatusOption {
  stageSubId: number;
  stageName: string;
  subName: string;
  label: string;
}

// ─── Unified filter params (preferred) ────────────────────────────────────────

export interface UnifiedPlanListParams {
  filtersArray?: string;
  search?: string;
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  export?: boolean;
}

// ─── Service methods ──────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of plans, optionally filtered.
 * Accepts either the unified filtersArray format or legacy per-param format.
 */
export function listPlans(params?: PlanListParams | UnifiedPlanListParams): Promise<PaginatedResponse<PlanListItem>> {
  return apiClient
    .get<PaginatedResponse<PlanListItem>>('/plans', { params })
    .then((r) => r.data);
}

export interface PlansRecipientsResult {
  total: number;
  uniqueEmailCount: number;
  uniquePhoneCount: number;
  recipients: { email: string; firstName: string; lastName: string; phone: string; patientId: number | null }[];
}

export function getPlansRecipients(params?: PlanListParams & { page?: number; perPage?: number }): Promise<PlansRecipientsResult> {
  return apiClient
    .get<{ data: PlansRecipientsResult }>('/plans/recipients', { params })
    .then((r) => r.data.data);
}

/**
 * Fetch the full plan record by ID.
 */
export function getPlan(id: number): Promise<Plan> {
  return apiClient
    .get<ApiResponse<Plan>>(`/plans/${id}`)
    .then((r) => r.data.data);
}

/**
 * Fetch the plan's progress pipeline data (per-account custom stages + rules).
 * Mirrors the legacy plan-stage-pipeline.php logic 1:1.
 */
export function getPlanProgressBar(id: number): Promise<PlanProgressBar> {
  return apiClient
    .get<ApiResponse<PlanProgressBar>>(`/plans/${id}/progress-bar`)
    .then((r) => r.data.data);
}

export interface CheckPlanTypeResult {
  allowed: boolean;
  reason?: 'duplicate' | 'pipeline';
  message?: string;
}

/**
 * Validate whether changing a plan's type is allowed. Checks for duplicate
 * plan types and pipeline compatibility (current stage reachable in new pipeline).
 */
export function checkPlanType(planId: number, planTypeId: number): Promise<CheckPlanTypeResult> {
  return apiClient
    .get<ApiResponse<CheckPlanTypeResult>>(`/plans/${planId}/check-plan-type`, {
      params: { planTypeId },
    })
    .then((r) => r.data.data);
}

/**
 * Fetch the available `stages_sub` options for the plan's currently selected
 * stage, plus the plan's current stageSubID. Used to populate the legacy
 * "Status" dropdown that lives directly under the progress pipeline.
 */
export function getPlanStageSubs(id: number): Promise<PlanStageSubsResponse> {
  return apiClient
    .get<ApiResponse<PlanStageSubsResponse>>(`/plans/${id}/stage-subs`)
    .then((r) => r.data.data);
}

/**
 * Fetch all plans for a specific patient.
 */
export function getPlansByPatient(patientId: number): Promise<PlanListItem[]> {
  return apiClient
    .get<ApiResponse<PlanListItem[]>>(`/patients/${patientId}/plans`)
    .then((r) => r.data.data);
}

/**
 * Create a new plan.
 */
export function createPlan(data: CreatePlanPayload): Promise<Plan> {
  return apiClient
    .post<ApiResponse<Plan>>('/plans', data)
    .then((r) => r.data.data);
}

/**
 * Update an existing plan (partial update supported). Returns the updated
 * plan, or — when the stage change spawned a revision (legacy stage 12) —
 * the new revision plan with `newPlanId` set to the new ID.
 */
export function updatePlan(id: number, data: UpdatePlanPayload): Promise<UpdatedPlan> {
  return apiClient
    .put<ApiResponse<UpdatedPlan>>(`/plans/${id}`, data)
    .then((r) => r.data.data);
}

export interface ChangeStagePayload {
  stageId: number;
  messageNotes?: string;
  alerts?: string;
  recipients?: string;
}

/**
 * Dedicated stage-change endpoint mirroring legacy events_log_processor_tk.php.
 * Runs the full trigger pipeline: task removal, stage branches, comm_log
 * (typeID=17), task materialization, plan update, and lastContact stamp.
 */
export function changePlanStage(id: number, data: ChangeStagePayload): Promise<UpdatedPlan> {
  return apiClient
    .post<ApiResponse<UpdatedPlan>>(`/plans/${id}/change-stage`, data)
    .then((r) => r.data.data);
}

/**
 * Delete a plan.
 */
export function deletePlan(id: number): Promise<void> {
  return apiClient.delete(`/plans/${id}`).then(() => undefined);
}

/**
 * Fetch plan counts per stage for pipeline summary.
 * Accepts the same filter params as the plan list to return filtered counts.
 */
export function getStageStats(params?: Partial<PlanListParams>): Promise<StageStats[]> {
  return apiClient
    .get<ApiResponse<StageStats[]>>('/plans/stage-stats', { params })
    .then((r) => r.data.data);
}

/**
 * Fetch available substage (status) options for filters.
 */
export function getStatusOptions(): Promise<StatusOption[]> {
  return apiClient
    .get<ApiResponse<StatusOption[]>>('/plans/status-options')
    .then((r) => r.data.data);
}

// ─── Plan history (audit log) ─────────────────────────────────────────────────

export interface PlanHistoryChange {
  field: string;
  newValue: string;
  verb: string;
}

export interface PlanHistoryEntry {
  date: string;
  userId: number;
  userName: string;
  changes: PlanHistoryChange[];
}

export function getPlanHistory(planId: number): Promise<PlanHistoryEntry[]> {
  return apiClient
    .get<ApiResponse<PlanHistoryEntry[]>>(`/plans/${planId}/history`)
    .then((r) => r.data.data);
}

// ─── Plan associated entities ─────────────────────────────────────────────────

export interface PlanAssociatedAppointment {
  appointmentId: number;
  startTime: string;
  endTime: string;
  appointmentTypeName: string;
  appointmentTypeColor: string;
  status: string;
  facilityName: string;
  /** scheduled_appointments.scheduledAppointmentDuration. */
  durationMinutes: number;
  /** Resource names, alphabetical; empty when none are booked. */
  resources: string[];
  linkedToPlan: boolean;
}

export interface PlanAssociatedMembership {
  recurringId: number;
  membershipTitle: string;
  status: string;
  curAmount: number;
  startDate: string;
  linkedToPlan: boolean;
}

export interface PlanAssociatedPackage {
  patientPackageId: number;
  packageName: string;
  purchaseDate: string;
  price: number;
  active: boolean;
  linkedToPlan: boolean;
}

export interface PlanAssociatedEntities {
  appointments: PlanAssociatedAppointment[];
  memberships: PlanAssociatedMembership[];
  packages: PlanAssociatedPackage[];
  productTypeIds: number[];
}

export function getPlanAssociatedEntities(planId: number): Promise<PlanAssociatedEntities> {
  return apiClient
    .get<ApiResponse<PlanAssociatedEntities>>(`/plans/${planId}/associated-entities`)
    .then((r) => r.data.data);
}
